/**
 * Redux slice for bank account information
 * Manages borrower bank account details for payment processing
 */

import { createSlice, PayloadAction } from "@reduxjs/toolkit";

interface BankState {
  bankName: string | null;
  accountNumber: string | null;
  accountBan: string | null;
  accountHolderName: string | null;
  swiftCode: string | null;
  accountType: string | null;
  bankAddress: string | null;
  bankCountry: string | null;
  currency: string | null;
}

const initialState: BankState = {
  bankName: null,
  accountNumber: null,
  accountBan: null,
  accountHolderName: null,
  swiftCode: null,
  accountType: null,
  bankAddress: null,
  bankCountry: null,
  currency: null,
};

/**
 * Generic reducer factory for simple field updates
 * Eliminates duplication for setters that just assign a value
 */
const createFieldSetter = <K extends keyof BankState>(field: K) => {
  return (state: BankState, action: PayloadAction<BankState[K]>) => {
    state[field] = action.payload;
  };
};

const BorbankSlice = createSlice({
  name: "BorBank",
  initialState,
  reducers: {
    // Bank identification
    setBankNameRedux: createFieldSetter("bankName"),
    setSwiftCodeReduct: createFieldSetter("swiftCode"),

    // Account details
    setAccountNoRedux: createFieldSetter("accountNumber"),
    setAccountBanRedux: createFieldSetter("accountBan"),
    setAccountHolderNameRedux: createFieldSetter("accountHolderName"),
    setAccountTypeRedux: createFieldSetter("accountType"),

    // Bank address details
    setBankAddressRedux: createFieldSetter("bankAddress"),
    setBankCountryRedux: createFieldSetter("bankCountry"),
    setCurrencyRedux: createFieldSetter("currency"),

    /**
     * Bulk update multiple fields at once
     * Useful for hydrating state from API response
     */
    hydrateBankState: (state, action: PayloadAction<Partial<BankState>>) => {
      return { ...state, ...action.payload };
    },

    /**
     * Reset to initial state
     */
    resetBankState: () => initialState,
  },
});

export const {
  setBankNameRedux,
  setAccountNoRedux,
  setAccountBanRedux,
  setAccountHolderNameRedux,
  setSwiftCodeReduct,
  setAccountTypeRedux,
  setBankAddressRedux,
  setBankCountryRedux,
  setCurrencyRedux,
  hydrateBankState,
  resetBankState,
} = BorbankSlice.actions;

export default BorbankSlice.reducer;
